/**
 * Registers the built-in seed providers. Called once before the first seed
 * lookup (idempotent). New domains add their `registerSeedProvider` calls
 * here as they land.
 */

import { registerSeedProvider } from './SeedRegistry';
import {
  additionalProcessTemplateSeeds,
  flowchartBasicSeed,
  swimlaneApprovalSeed,
  bpmnOrderSeed,
  newHireOnboardingSeed,
  incidentPostmortemSeed,
  carePathwaySeed,
} from './seeds/processSeeds';
import { swotBasicSeed, bmcBasicSeed, okrCascadeSeed, comparisonMatrixSeed } from './seeds/strategySeeds';
import { kanbanBasicSeed, retroBasicSeed } from './seeds/projectSeeds';
import {
  auditReadinessSeed,
  riskRegisterSeed,
  vendorRiskMapSeed,
  iso27001AnnexAChecklistSeed,
  ismsScopeMapSeed,
} from './seeds/riskSeeds';
import {
  org25SampleSeed,
  orgManualStarterSeed,
  reorgPlanSeed,
} from '../../domains/orgchart/seeds/org25SampleSeed';
import { orgTemplateSeeds } from '../../domains/orgchart/seeds/orgTemplateSeeds';
import { orgFromSourceSeed } from '../../domains/orgchart/seeds/orgFromSourceSeed';
import { networkTemplateSeeds } from '../../domains/network/seeds/networkTemplateSeeds';

let registered = false;

export function registerDefaultSeeds(): void {
  if (registered) return;
  registered = true;

  // Flowchart-domain starters — RDM (`type: process`/`bpmn` + a `pool:` field
  // for lanes), materialized through the SAME production stack as rdm_apply /
  // Toolbar import: parseAndValidate → toLayoutedRuntimeModel → ApplyRdmSceneCommand.
  registerSeedProvider(flowchartBasicSeed);
  registerSeedProvider(swimlaneApprovalSeed);
  registerSeedProvider(bpmnOrderSeed);
  registerSeedProvider(newHireOnboardingSeed);
  registerSeedProvider(incidentPostmortemSeed);
  registerSeedProvider(carePathwaySeed);
  for (const seed of additionalProcessTemplateSeeds) registerSeedProvider(seed);

  // Table-RDM starters (strategy / projects / risk sections) — `type: table`
  // documents hosted on the flowchart canvas; the hydrator docks content
  // into schema cells, no ELK pass.
  registerSeedProvider(swotBasicSeed);
  registerSeedProvider(bmcBasicSeed);
  registerSeedProvider(okrCascadeSeed);
  registerSeedProvider(comparisonMatrixSeed);
  registerSeedProvider(kanbanBasicSeed);
  registerSeedProvider(retroBasicSeed);
  registerSeedProvider(auditReadinessSeed);
  registerSeedProvider(riskRegisterSeed);
  registerSeedProvider(vendorRiskMapSeed);
  registerSeedProvider(iso27001AnnexAChecklistSeed);
  registerSeedProvider(ismsScopeMapSeed);

  // Org — manual and CSV sample starters as `commands` providers, plus
  // the reorg-scenario trigger variant.
  registerSeedProvider(orgManualStarterSeed);
  registerSeedProvider(org25SampleSeed);
  registerSeedProvider(reorgPlanSeed);
  registerSeedProvider(orgFromSourceSeed);
  for (const seed of orgTemplateSeeds) registerSeedProvider(seed);

  // Network — the template registry wrapped as `commands` providers.
  for (const seed of networkTemplateSeeds) registerSeedProvider(seed);

  // Genogram keeps its own wizard (template/clone seeds are server-side).
}
